import { FC, Suspense, useMemo, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import WeeklyGoalHeader from './WeeklyGoalHeader';
import { GoalValue } from './WeeklyGoalWrapper';
import WeeklyGoalPassedSkillModal from './WeeklyGoalPassedSkillModal';
import { WeeklyGoalIndicatorQuery } from '../../graphQLOperations/__generated__/WeeklyGoalIndicatorQuery.graphql';
import { selectedStudentSkillStates } from '../../redux/reducers/studentSkillsSlice';
import { useAppSelector } from '../../redux/hooks';
import { getWeeklyGoalIndicator } from '../../graphQLOperations/WeeklyGoal';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';

interface Props {
  queryRef: PreloadedQuery<WeeklyGoalIndicatorQuery, Record<string, unknown>>;
  overrideData: GoalValue[] | null;
}

const WeeklyGoalContent: FC<Props> = ({ queryRef, overrideData }) => {
  const studentSkills = useAppSelector(selectedStudentSkillStates);
  const queryData = queryRef ? usePreloadedQuery<WeeklyGoalIndicatorQuery>(getWeeklyGoalIndicator, queryRef) : null;
  const [showPassedSkillModal, setShowPassedSkillModal] = useState(false);

  const baseGoalValues = useMemo(() => {
    return (
      queryData?.studentWeeklyGoalIndicator?.map(el => ({
        code: el.code,
        name: el.name,
        skillType: studentSkills
          ? studentSkills?.find(s => s.skill.code === el.code)?.targetSkillType || 'Support'
          : 'Support',
      })) || []
    );
  }, [queryData]);

  const finalGoalValues = useMemo(() => {
    const map = new Map<string, GoalValue>();
    baseGoalValues.forEach(goal => {
      map.set(goal.code, goal);
    });
    overrideData?.forEach(goal => {
      map.set(goal.code, goal);
    });

    return Array.from(map.values());
  }, [baseGoalValues, overrideData]);

  return (
    <Suspense fallback={<ThreeDotLoader dark />}>
      <WeeklyGoalHeader
        goalValues={finalGoalValues}
        showPassedSkillModal={showPassedSkillModal}
        setShowPassedSkillModal={setShowPassedSkillModal}
      />
      {showPassedSkillModal && (
        <WeeklyGoalPassedSkillModal
          setVisible={setShowPassedSkillModal}
          visible={showPassedSkillModal}
          // adding 1 to the length to account for the newly added skill that triggered the modal
          goalValuesLength={finalGoalValues.length + 1}
        />
      )}
    </Suspense>
  );
};

export default WeeklyGoalContent;
